<template>
  <div
    class="scenario-grid"
    :aria-label="ariaLabel"
    :style="gridStyle"
  >
    <article
      v-for="item in items"
      :key="item.key"
      class="scenario-grid__card"
    >
      <img
        class="scenario-grid__media"
        :src="item.image"
        :alt="item.alt || item.imageAlt || item.title"
        loading="lazy"
      />
      <div class="scenario-grid__copy">
        <h3>{{ item.title }}</h3>
        <p v-if="item.descriptionHtml" data-text-reveal="off" v-html="item.descriptionHtml"></p>
        <p v-else-if="item.description">{{ item.description }}</p>
      </div>
    </article>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

export interface ScenarioGridItem {
  key: string
  title: string
  image: string
  alt?: string
  imageAlt?: string
  description?: string
  descriptionHtml?: string
}

const props = withDefaults(defineProps<{
  items: ScenarioGridItem[]
  ariaLabel?: string
  desktopColumns?: number
  tabletColumns?: number
  mobileColumns?: number
  gap?: string
  cardRadius?: string
  cardAspectRatio?: string
  titleFontSize?: string
  titleLineHeight?: string
  descriptionFontSize?: string
  descriptionLineHeight?: string
  descriptionFontWeight?: string
  descriptionFontFamily?: string
  copyInsetX?: string
  copyInsetBottom?: string
}>(), {
  ariaLabel: '',
  desktopColumns: 3,
  tabletColumns: 2,
  mobileColumns: 1,
  gap: '24px',
  cardRadius: 'var(--pixso-radius-lg)',
  cardAspectRatio: '584 / 382',
  titleFontSize: 'var(--pixso-fs-36)',
  titleLineHeight: 'var(--pixso-lh-36)',
  descriptionFontSize: '16px',
  descriptionLineHeight: '24px',
  descriptionFontWeight: '400',
  descriptionFontFamily: 'HarmonyOS Sans',
  copyInsetX: '24px',
  copyInsetBottom: '24px'
})

const gridStyle = computed(() => ({
  '--scenario-grid-columns-desktop': String(props.desktopColumns),
  '--scenario-grid-columns-tablet': String(props.tabletColumns),
  '--scenario-grid-columns-mobile': String(props.mobileColumns),
  '--scenario-grid-gap': props.gap,
  '--scenario-grid-card-radius': props.cardRadius,
  '--scenario-grid-card-aspect-ratio': props.cardAspectRatio,
  '--scenario-grid-title-font-size': props.titleFontSize,
  '--scenario-grid-title-line-height': props.titleLineHeight,
  '--scenario-grid-description-font-size': props.descriptionFontSize,
  '--scenario-grid-description-line-height': props.descriptionLineHeight,
  '--scenario-grid-description-font-weight': props.descriptionFontWeight,
  '--scenario-grid-description-font-family': props.descriptionFontFamily,
  '--scenario-grid-copy-inset-x': props.copyInsetX,
  '--scenario-grid-copy-inset-bottom': props.copyInsetBottom
}))
</script>

<style scoped>
.scenario-grid {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(var(--scenario-grid-columns-desktop), minmax(0, 1fr));
  gap: var(--scenario-grid-gap);
}

.scenario-grid__card {
  position: relative;
  aspect-ratio: var(--scenario-grid-card-aspect-ratio);
  overflow: hidden;
  border-radius: var(--scenario-grid-card-radius);
  background: var(--pixso-color-surface);
  color: var(--pixso-color-text);
  isolation: isolate;
}

.scenario-grid__card::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 38%, rgba(0, 0, 0, 0.78) 100%);
  pointer-events: none;
}

.scenario-grid__media {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.scenario-grid__copy {
  position: absolute;
  left: var(--scenario-grid-copy-inset-x);
  right: var(--scenario-grid-copy-inset-x);
  bottom: var(--scenario-grid-copy-inset-bottom);
  z-index: 2;
  color: var(--pixso-color-text);
  text-shadow: 0 2px 14px rgba(0, 0, 0, 0.64);
}

.scenario-grid__copy h3 {
  margin: 0;
  font-family: var(--pixso-font-cn);
  font-size: var(--pixso-fs-36);
  font-weight: 700;
  line-height: var(--pixso-lh-36);
}

.scenario-grid__copy p {
  margin: var(--pixso-gap-xs) 0 0;
  font-family: var(--scenario-grid-description-font-family);
  font-size: var(--pixso-fs-16);
  line-height: var(--pixso-lh-16);
  font-weight: var(--scenario-grid-description-font-weight);
  color: rgba(255, 255, 255, 0.82);
}

@media (max-width: 1200px) {
  .scenario-grid {
    grid-template-columns: repeat(var(--scenario-grid-columns-tablet), minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .scenario-grid {
    grid-template-columns: repeat(var(--scenario-grid-columns-mobile), minmax(0, 1fr));
  }

  .scenario-grid__copy h3 {
    font-size: var(--pixso-fs-24);
    line-height: var(--pixso-lh-24);
  }

  .scenario-grid__copy p {
    font-size: var(--pixso-fs-14);
    line-height: var(--pixso-lh-14);
  }
}
</style>
